import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth } from '@aws-amplify/auth';
import { from, Observable, of, Subject, timer } from 'rxjs';
import { mergeMap, tap, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { OrderRequest } from '../models/order-request.model';
import { PatientCompliance } from '../models/patient-compliance.model';
import {
  PampAssessmentTodoTemplate,
  PampListResponse,
  PampQuestionnaireRecurrence
} from '../models/pamp-todo.model';
import { CheckinData } from '../models/patient-app-widget.model';

@Injectable({
  providedIn: 'root'
})
export class PAMPRestAPIService {
  public pampTodoCreated$ = new Subject<string | void>();
  private productOrderStatsCache: {
    [clinicId: string]: Observable<{ allOrders: Partial<OrderRequest>[] }>;
  } = {};
  private checkinStatsCache: { [clinicId: string]: Observable<CheckinData[]> } = {};

  private baseUrl =
    environment.pampRestAPIEndpoint || 'https://g4fx0shwbc.execute-api.us-east-1.amazonaws.com/dev';

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): Observable<HttpHeaders> {
    return from(Auth.currentSession()).pipe(
      mergeMap(session => {
        const token = session.getIdToken().getJwtToken();
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        });
        return of(headers);
      })
    );
  }

  public getPatientCompliance(patientId: string): Observable<PatientCompliance> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => {
        return this.http.get<PatientCompliance>(`${this.baseUrl}/compliance/patient/${patientId}`, {
          headers
        });
      })
    );
  }

  public getPAMPPatientOrderRequests(patientId: string): Observable<OrderRequest[]> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => {
        return this.http.get<OrderRequest[]>(
          `${this.baseUrl}/order-requests/patient/${patientId}`,
          {
            headers
          }
        );
      })
    );
  }

  public getQuestionnaireRecurrences(
    ownerId: string
  ): Observable<PampListResponse<PampQuestionnaireRecurrence>> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => {
        const url = `${this.baseUrl}/pampQRERecurrence?ownerId=${ownerId}`;
        return this.http.get<PampListResponse<PampQuestionnaireRecurrence>>(url, { headers });
      })
    );
  }

  public getPampTodos(
    ownerId: string,
    clinicId: string,
    category: string = 'assessment'
  ): Observable<PampAssessmentTodoTemplate[]> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => {
        const url = `${this.baseUrl}/pamp-todos?ownerId=${ownerId}&clinicId=${clinicId}&category=${category}`;
        return this.http.get<PampAssessmentTodoTemplate[]>(url, { headers });
      })
    );
  }

  public createPampTodoForPatient(payload: {
    patientId: string;
    clinicId: string;
    ownerId?: string;
    enrollmentCode?: string;
    expiresInDays?: number;
    data: any[];
  }): Observable<any> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => {
        return this.http.post<any>(`${this.baseUrl}/pampTodoForPatient`, payload, { headers });
      }),
      tap(() => {
        this.pampTodoCreated$.next(payload.patientId);
      })
    );
  }

  public getPampTodoForPatient(patientId: string, enrollmentCode?: string): Observable<any[]> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => {
        let url = `${this.baseUrl}/pampTodoForPatient?patientId=${patientId}`;
        if (enrollmentCode) {
          url += `&enrollmentCode=${encodeURIComponent(enrollmentCode)}`;
        }
        return this.http.get<any[]>(url, { headers });
      })
    );
  }

  public getProductOrderStats(
    clinicId: string
  ): Observable<{ allOrders: Partial<OrderRequest>[] }> {
    if (this.productOrderStatsCache[clinicId]) {
      return this.productOrderStatsCache[clinicId];
    }

    const req = this.getAuthHeaders().pipe(
      mergeMap(headers => {
        const url = `${this.baseUrl}/dashboard/product-orders?clinicId=${clinicId}`;
        return this.http.get<{
          allOrders: Partial<OrderRequest>[];
        }>(url, { headers });
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.productOrderStatsCache[clinicId] = req;
    timer(5000).subscribe(() => delete this.productOrderStatsCache[clinicId]);

    return req;
  }

  public getCheckinStats(clinicId: string): Observable<CheckinData[]> {
    if (this.checkinStatsCache[clinicId]) {
      return this.checkinStatsCache[clinicId];
    }

    const req = this.getAuthHeaders().pipe(
      mergeMap(headers => {
        const url = `${this.baseUrl}/dashboard/checkins?clinicId=${clinicId}`;
        return this.http.get<CheckinData[]>(url, { headers });
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.checkinStatsCache[clinicId] = req;
    timer(5000).subscribe(() => delete this.checkinStatsCache[clinicId]);

    return req;
  }
}
